import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { setToastMessage } from '@stores/App/slice';
import { PaginationConfig } from '@utils/types/PaginationConfig';
import { PricingMethodology, SEARCH_DEBOUNCE_TIME, TOAST_TYPES } from '@constants/constants';
import useDebounce from '@hooks/useDebounce';
import { GenericTable } from '@components';
import { useLazyDealsCsvDownloadQuery, useLazyGetGamesQuery } from '@api/games';
import { DealsListingResponse } from '@api/types/games';
import { GameData } from '@api/types/SelfServeDetails';
import { constructFetchGamesQueryString } from '@utils/common';
import { Column } from '../../../components/GenericTable/GenericTable';
import DealSetupNewDeal from '../../Dashboard/components/DealSetup/DealSetupNewDeal';
import TableFilterDropdown from '../../../components/TableFilterDropdown';
import Pagination from '../../Dashboard/components/Pagination';
import CustomButton from '../../../components/CustomButton/CustomButton';

const dealsTableMetadata = {
  chartType: 'TABLE',
  columns: [
    {
      id: 'gameId',
      name: 'Game Id',
      type: 'string',
    },
    {
      id: 'clientCode',
      name: 'Client Code',
      type: 'string',
    },
    {
      id: 'name',
      name: 'Game Name',
      type: 'string',
    },
    {
      id: 'companyName',
      name: 'Client Name',
      type: 'string',
    },
    {
      id: 'dealName',
      name: 'Deal Name',
      type: 'string',
    },
    {
      id: 'refreshRequired',
      name: 'Monthly Refresh',
      type: 'string',
    },
    {
      id: 'category',
      name: 'Category',
      type: 'string',
    },
    {
      id: 'genere',
      name: 'Genre',
      type: 'string',
    },
    {
      id: 'interest',
      name: 'Pricing',
      type: 'string',
    },
  ],
};

const dealsTableMappedColumns = [
  {
    key: 'gameId',
    label: 'Game Id',
  },
  {
    key: 'clientCode',
    label: 'Client Code',
  },
  {
    key: 'name',
    label: 'Game Name',
  },
  {
    key: 'companyName',
    label: 'Client Name',
  },
  {
    key: 'dealName',
    label: 'Deal Name',
  },
  {
    key: 'refreshRequired',
    label: 'Monthly Refresh',
  },
  {
    key: 'category',
    label: 'Category',
  },
  {
    key: 'genere',
    label: 'Genre',
  },
  {
    key: 'interest',
    label: 'Pricing',
  },
];

const refreshOptions = [
  { label: 'On', value: 'true' },
  { label: 'Off', value: 'false' },
];

const DealSetup: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // States
  const [showDealSetupEditDeal, setShowDealSetupEditDeal] = useState<boolean>(false);
  const [showDealSetupNewDeal, setShowDealSetupNewDeal] = useState<boolean>(false);
  const [deals, setDeals] = useState<any[] | null>(null);
  const [dealsPagination, setDealsPagination] = useState<PaginationConfig>({
    pageNumber: 1,
    pageSize: 50,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [selectedRefreshFilters, setSelectedRefreshFilters] = useState<string[]>([]);

  // APIs
  const [getGames, { isLoading: isDealsLoading }] = useLazyGetGamesQuery();
  const [getDealsCsv] = useLazyDealsCsvDownloadQuery();

  useEffect(() => {
    fetchDeals({
      pageNumber: 1,
      search: '',
      selectedRefreshFilters: [],
    });
  }, []);

  const debouncedSearch = useDebounce(
    (value: string) =>
      fetchDeals({
        pageNumber: 1,
        search: value,
      }),
    SEARCH_DEBOUNCE_TIME,
  );

  const updateDealTableData = (response: DealsListingResponse) => {
    const formattedDeals = response.data.items.map((deal) => {
      let interestType = '-';

      const pricingMethodology = deal?.dealDetails?.pricingMethodology;
      if (!pricingMethodology) {
        interestType = '-';
      } else if (pricingMethodology === PricingMethodology.SIMPLE_INTEREST) {
        interestType = 'SI';
      } else if (pricingMethodology === PricingMethodology.DYNAMIC_RETURN_CAP) {
        interestType = 'DP';
      } else if (pricingMethodology === PricingMethodology.DYNAMIC_RETURN_CAP_V2) {
        interestType = 'DP V2';
      } else {
        interestType = 'Custom';
      }

      return {
        id: deal?.gameDetails?.gameId,
        gameId: deal?.gameDetails?.gameId || '-',
        clientCode: deal?.gameDetails?.clientCode || '-',
        name: deal?.gameDetails?.name || '-',
        companyName: deal?.gameDetails?.companyName || '-',
        dealName: deal?.gameDetails?.dealName || '-',
        refreshRequired: deal?.gameDetails?.refreshRequired ? 'On' : 'Off',
        category: deal?.gameDetails?.category || '-',
        genere: deal?.gameDetails?.genre || '-',
        interest: interestType,
      };
    });

    setDeals(formattedDeals);
  };

  const updateFilters = (args: {
    pageNumber: number;
    pageSize: number;
    search: string;
    selectedRefreshFilters: string[];
    total: number;
  }) => {
    setDealsPagination({
      ...dealsPagination,
      pageNumber: args.pageNumber,
      pageSize: args.pageSize,
      total: args.total,
    });
  };

  const fetchDeals = async (
    inputProps: {
      pageNumber?: number;
      search?: string;
      selectedRefreshFilters?: string[];
    } = {},
  ) => {
    const args = {
      pageNumber: dealsPagination.pageNumber,
      pageSize: dealsPagination.pageSize,
      search: searchValue,
      selectedRefreshFilters: selectedRefreshFilters,
      ...inputProps,
    };

    args.pageNumber = args.pageNumber - 1;
    const queryString = constructFetchGamesQueryString(args);

    try {
      const response = await getGames(queryString).unwrap();
      updateDealTableData(response);
      updateFilters({
        pageNumber: args.pageNumber + 1,
        pageSize: args.pageSize,
        search: args.search,
        selectedRefreshFilters: args.selectedRefreshFilters,
        total: response.data.totalElements,
      });
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const handleReset = () => {
    setSearchValue('');
    setSelectedRefreshFilters([]);
    fetchDeals({
      pageNumber: 1,
      search: '',
      selectedRefreshFilters: [],
    });
  };

  const handleCloseEditDeal = () => {
    setShowDealSetupEditDeal(false);
    fetchDeals();
  };

  const handleCloseNewDeal = () => {
    setShowDealSetupNewDeal(false);
    setSearchValue('');
    setSelectedRefreshFilters([]);
    fetchDeals({
      pageNumber: 1,
      search: '',
      selectedRefreshFilters: [],
    });
  };

  const handleEditButtonClick = (gameId: string) => {
    setSelectedGameId(gameId);
    setShowDealSetupEditDeal(true);
  };

  const handleNewDealButtonClick = () => {
    setSelectedGameId('');
    setShowDealSetupNewDeal(true);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSelectedRefreshFilters = (selected: string[]) => {
    let filters: string[] = [];

    if (selected.length === 0) {
      filters = [];
    } else if (selected.length === 1) {
      filters = [selected[0]];
    } else if (selected.length === 2) {
      filters = [selected[1]];
    }

    setSelectedRefreshFilters(filters);
    fetchDeals({
      pageNumber: 1,
      selectedRefreshFilters: filters,
    });
  };

  const conditionallyMappedColumn = dealsTableMappedColumns.map((col: any) => {
    if (col.key === 'refreshRequired') {
      return {
        ...col,
        render: (key: string, row: any) => {
          const value = row[col.key];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1B1C17',
                }}
              >
                {value}
              </Typography>
            </Box>
          );
        },
        renderHeader: (column: Column<GameData>) => {
          return (
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={'0.5rem'}
            >
              <span>{column.label}</span>
              <TableFilterDropdown
                setSelected={handleSelectedRefreshFilters}
                selected={selectedRefreshFilters}
                options={refreshOptions}
              />
            </Stack>
          );
        },
      };
    }
    return col;
  });

  const containerStyles = {
    display: 'flex',
    justifyContent: isMobile ? 'center' : 'space-between',
    alignItems: isMobile ? 'flex-end' : 'center',
    pb: '1rem',
    flexDirection: isMobile ? 'column' : 'row',
  };

  const searchContainerStyles = {
    flex: 1,
    width: isMobile ? '100%' : '80%',
  };

  const searchPaperStyles = {
    height: 45,
    px: '19px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '28px',
    backgroundColor: theme.palette.custom_field_background,
    width: isMobile ? '100%' : '80%',
    boxShadow: 'none',
  };

  const inputStyles = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  };

  const renderSearchIcon = () => {
    if (searchValue) {
      return (
        <IconButton
          size="small"
          onClick={() =>
            handleSearchInputChange({
              target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          sx={{ p: 0.5 }}
          aria-label="Clear search"
        >
          <CloseIcon />
        </IconButton>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SearchIcon />
      </Box>
    );
  };

  const handleDownload = async () => {
    try {
      const refreshFilterExists = selectedRefreshFilters.length > 0;

      const params: {
        search: string;
        refreshRequired?: boolean;
      } = {
        search: searchValue,
        ...(refreshFilterExists && {
          refreshRequired: selectedRefreshFilters[0] === 'true' ? true : false,
        }),
      };

      const response = await getDealsCsv(params).unwrap();

      const csvUrl = response?.data.url;

      if (csvUrl) {
        const link = document.createElement('a');
        link.href = csvUrl;
        link.download = 'deal_setup.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('No data received.');
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Unable to download CSV, please try again later.',
        }),
      );
    }
  };

  return (
    <>
      {/* {dashboardLoader && <FullPageLoader hasSideNav={true} />} */}
      <Box
        id="pvx-funding-details-container"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          overflow: 'auto',
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1.3rem',
            marginBottom: '2rem',
          }}
        >
          <Typography
            fontSize="2rem"
            fontWeight="600"
            lineHeight="1.5rem"
            fontFamily="Manrope,sans-serif"
            sx={{ color: '#1B1C17' }}
          >
            Deal Setup
          </Typography>
        </Box>
        <Box sx={containerStyles}>
          <Box sx={searchContainerStyles}>
            <Paper component="form" sx={searchPaperStyles}>
              <InputBase
                inputRef={searchInputRef}
                placeholder="Search Game Name/ Client Name/ Deal Name/ Game ID"
                value={searchValue}
                onChange={handleSearchInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                sx={inputStyles}
                fullWidth
              />
              {renderSearchIcon()}
            </Paper>
          </Box>

          <CustomButton
            color="#F6F8F9"
            backgroundColor="#2F736E"
            name="New Deal"
            startIcon={AddIcon}
            onClick={handleNewDealButtonClick}
            height="40px"
          />
        </Box>
        {showDealSetupNewDeal || showDealSetupEditDeal ? (
          <DealSetupNewDeal
            selectedGameId={selectedGameId}
            handleEditDealCancel={handleCloseEditDeal}
            handleNewDealCancel={handleCloseNewDeal}
          />
        ) : (
          <Box
            key={'All Deals'}
            sx={{
              width: '100%',
              height: 'fit-content',
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '0.75rem',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                marginBottom: '1rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '1rem',
                  lineHeight: '1.375rem',
                  fontWeight: 700,
                  color: '#162936',
                }}
              >
                Deals
              </Typography>
              <Stack direction={'row'} gap={'0.5rem'}>
                <Pagination
                  page={dealsPagination.pageNumber}
                  size={dealsPagination.pageSize}
                  total={dealsPagination.total}
                  handleChange={(pageNumber: number) => fetchDeals({ pageNumber })}
                  fileName={''}
                  chartId={''}
                  submoduleId={''}
                  filterSelection={{}}
                />
                <IconButton
                  onClick={handleDownload}
                  sx={{
                    color: '#2E736E',
                    padding: '4px',
                  }}
                >
                  <FileDownloadOutlinedIcon />
                </IconButton>
                <IconButton onClick={handleReset} sx={{ color: '#2E736E' }}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Box>
            <GenericTable
              data={deals || []}
              columns={conditionallyMappedColumn}
              meta={dealsTableMetadata}
              actions={[
                {
                  icon: 'edit-icon-green.svg',
                  action: (id) => handleEditButtonClick(id),
                },
              ]}
              isLoading={isDealsLoading}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default DealSetup;
